import { useEffect, useState } from 'react';
import { SiInviteError, currentRoom, fetchInvitableSis, inviteSi } from './api';
/**
 * Kamo Meet "Invite an SI": the panel's state, outside Redux on purpose. A feature reducer would be typechecked for
 * the native app through app/types.ts, and this feature is web only.
 *
 * Live: while the panel is open, or an invite is on its way, the list is read again every few seconds and whenever
 * someone joins or leaves the call ({@link nudge}), so an SI that comes in another way drops out of the list and one
 * that leaves comes back.
 */
const POLL_MS = 3000;
const JOINED_LINGER_MS = 1800;
const JOIN_GIVE_UP_MS = 75000;
const RETRY_PROBE_MS = 30000;
let state = { availability: 'unknown',
    loading: false,
    open: false,
    pending: {} };
const listeners = new Set();
let timer;
let inFlight;
let roomOverride;
/**
 * Replaces the state and tells every subscriber.
 *
 * @param {Partial<ISiInviteState>} patch - What changes.
 * @returns {void}
 */
function set(patch) {
    state = { ...state,
        ...patch };
    listeners.forEach(l => l());
}
/**
 * The room the calls are about.
 *
 * @returns {string}
 */
function room() {
    return roomOverride ?? currentRoom();
}
/**
 * Subscribes to the state.
 *
 * @param {Function} l - The listener.
 * @returns {Function} Unsubscribes.
 */
export function subscribe(l) {
    listeners.add(l);
    return () => {
        listeners.delete(l);
    };
}
/**
 * The state now.
 *
 * @returns {ISiInviteState}
 */
export function getState() {
    return state;
}
/**
 * The state, for a component.
 *
 * @returns {ISiInviteState}
 */
export function useSiInviteState() {
    const [snapshot, setSnapshot] = useState(state);
    useEffect(() => {
        setSnapshot(state);
        return subscribe(() => setSnapshot(state));
    }, []);
    return snapshot;
}
/**
 * Whether the list should be followed closely right now.
 *
 * @returns {boolean}
 */
function following() {
    return state.open || Object.values(state.pending).some(p => p.phase === 'inviting' || p.phase === 'joining');
}
/**
 * Plans the next read.
 *
 * @returns {void}
 */
function schedule() {
    if (timer) {
        clearTimeout(timer);
        timer = undefined;
    }
    if (state.availability === 'no') {
        return;
    }
    if (state.availability === 'unknown') {
        timer = setTimeout(() => refresh(), RETRY_PROBE_MS);
        return;
    }
    if (following()) {
        timer = setTimeout(() => refresh(), POLL_MS);
    }
}
/**
 * Lets a "Joined" card linger a moment, then drops it (the server no longer lists that SI).
 *
 * @param {string} id - The SI.
 * @returns {void}
 */
function forgetJoinedLater(id) {
    setTimeout(() => {
        const next = { ...state.pending };
        if (next[id]?.phase === 'joined') {
            delete next[id];
            set({ pending: next });
        }
    }, JOINED_LINGER_MS);
}
/**
 * Settles the invites this member is following against what the server now says.
 *
 * @param {ISiInviteListing} listing - The new list.
 * @returns {Object} The pending invites, settled.
 */
function reconcile(listing) {
    const now = Date.now();
    const pending = { ...state.pending };
    for (const [id, p] of Object.entries(pending)) {
        if (p.phase !== 'joining') {
            continue;
        }
        const here = listing.present.find(x => x.memberId === id);
        if (here?.state === 'IN_MEETING') {
            pending[id] = { ...p,
                phase: 'joined',
                since: now };
            forgetJoinedLater(id);
            continue;
        }
        if (here) {
            continue;
        }
        const listed = listing.sis.find(x => x.memberId === id);
        if (listed?.lastFailure) {
            pending[id] = { ...p,
                phase: 'failed',
                reason: listed.lastFailure,
                si: listed,
                since: now };
        }
        else if (now - p.since > JOIN_GIVE_UP_MS) {
            pending[id] = { ...p,
                phase: 'failed',
                reason: 'DIDNT_JOIN',
                since: now };
        }
    }
    return pending;
}
/**
 * Reads the list again. Concurrent calls share one read.
 *
 * @returns {Promise<void>}
 */
export function refresh() {
    if (inFlight) {
        return inFlight;
    }
    const r = room();
    if (!r) {
        set({ availability: 'no' });
        return Promise.resolve();
    }
    set({ loading: true });
    inFlight = fetchInvitableSis(r)
        .then(listing => {
        set({ availability: 'yes',
            error: undefined,
            listing,
            loading: false,
            pending: reconcile(listing) });
    })
        .catch((e) => {
        const err = e instanceof SiInviteError ? e : new SiInviteError(0, 'NETWORK');
        // Not signed in, an SI's own session, not in this meeting, or a room that is not a Kamo meeting: no button.
        if (err.status === 401 || err.status === 403 || err.status === 404) {
            set({ availability: 'no',
                loading: false,
                open: false });
        }
        else {
            set({ error: err.reason,
                loading: false });
        }
    })
        .finally(() => {
        inFlight = undefined;
        schedule();
    });
    return inFlight;
}
/**
 * Someone joined or left the call: read the list again soon if anyone is looking.
 *
 * @returns {void}
 */
export function nudge() {
    if (state.availability === 'yes' && following()) {
        refresh();
    }
}
/**
 * Opens or closes the panel.
 *
 * @param {boolean} open - Open it.
 * @returns {void}
 */
export function setOpen(open) {
    set({ open });
    if (open) {
        refresh();
    }
    else {
        schedule();
    }
}
/**
 * Brings this SI in. A second click while the first is on its way does nothing here, and the server makes sure only
 * one copy of the SI ever joins whoever asks.
 *
 * @param {IInvitableSi} si - The SI.
 * @returns {Promise<void>}
 */
export async function invite(si) {
    const had = state.pending[si.memberId];
    if (had && (had.phase === 'inviting' || had.phase === 'joining' || had.phase === 'joined')) {
        return;
    }
    set({ pending: { ...state.pending,
            [si.memberId]: { phase: 'inviting',
                si,
                since: Date.now() } } });
    try {
        const r = await inviteSi(room(), si.memberId);
        set({ pending: { ...state.pending,
                [si.memberId]: { phase: 'joining',
                    si,
                    since: Date.now() } } });
        if (r?.state === 'IN_MEETING') {
            // already here: let the next read settle it to "joined"
        }
    }
    catch (e) {
        const err = e instanceof SiInviteError ? e : new SiInviteError(0, 'NETWORK');
        set({ pending: { ...state.pending,
                [si.memberId]: { phase: 'failed',
                    reason: err.reason,
                    si,
                    since: Date.now() } } });
    }
    refresh();
}
/**
 * Forgets a failed invite, so its card goes back to "Invite".
 *
 * @param {string} memberId - The SI.
 * @returns {void}
 */
export function dismiss(memberId) {
    const next = { ...state.pending };
    delete next[memberId];
    set({ pending: next });
}
/**
 * Test and screenshot hook: replaces the state and the room.
 *
 * @param {Partial<ISiInviteState>} patch - The state.
 * @param {string} forRoom - The room.
 * @returns {void}
 */
export function __setForTests(patch, forRoom) {
    roomOverride = forRoom;
    set(patch);
}
